import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PanelLayout from "layouts/PanelLayout";
import AddDocumentForm from "components/common/AddDocumentForm";
import { TbSmartHome } from "react-icons/tb";
import { GoPlus } from "react-icons/go";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { IoArrowDownCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE = "https://algoritm-api-edu.uz";
const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const DocumentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const subjectFromState = location.state?.subject;

  const [subject, setSubject] = useState(subjectFromState || null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);

  const fetchDocuments = async () => {
    if (!subject?.slug) return;

    setLoading(true);
    try {
      const res = await api.get(
        `/api/teacher/Subjects/GetDocumentList/${subject.slug}/documents`
      );

      const docList = res.data?.rows || res.data?.data || res.data || [];

      setDocuments(
        docList.map((doc) => ({
          id: doc.id || doc.documentId,
          title: doc.title || doc.name || "Nomsiz hujjat",
          description: doc.description || doc.desc || "",
          category: doc.category || "",
          active: doc.active ?? true,
          fileUrl: doc.fileUrl || doc.url || doc.file,
          imageUrl: doc.imageUrl || doc.thumbnail,
        }))
      );
    } catch (err) {
      toast.error("Hujjatlar yuklanmadi");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subjectFromState) setSubject(subjectFromState);
  }, [subjectFromState]);

  useEffect(() => {
    if (subject?.slug) fetchDocuments();
  }, [subject?.slug]);

  const toggleMenu = (id) => {
    setActiveMenu(activeMenu === id ? null : id);
  };

  const handleAddNew = () => {
    setEditingDocument(null);
    setShowForm(true);
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    setShowForm(true);
  };

  const handleSubmit = async (formData) => {
    if (!subject?.slug) {
      toast.error("Fan slug'i topilmadi");
      return;
    }

    try {
      const data = new FormData();

      data.append("slug", subject.slug);

      data.append("title", formData.title);
      data.append("description", formData.description || "");
      data.append("category", formData.category || "");
      data.append("active", formData.active ? "true" : "false");

      if (formData.file) {
        data.append("Document", formData.file);
        data.append("DocumentName", formData.file.name);
      }

      if (formData.image) {
        data.append("image", formData.image);
      }
      console.log("Yuborilayotgan slug (body):", subject.slug);
      console.log("FormData tarkibi:");
      for (let [key, value] of data.entries()) {
        console.log(`  ${key}: ${value instanceof File ? value.name : value}`);
      }

      let res;

      if (editingDocument) {
        res = await api.put(
          `/api/teacher/Subjects/UpdateDocument/${subject.slug}/documents/${editingDocument.id}`,
          data
        );
      } else {
        res = await api.post(
          `/api/teacher/Subjects/AddDocument/${subject.slug}/documents`,
          data
        );
      }

      toast.success(editingDocument ? "Yangilandi" : "Qo'shildi");
      setShowForm(false);
      setEditingDocument(null);
      fetchDocuments();
    } catch (err) {
      console.error("API xatosi:", err.response?.data);
      toast.error(
        err.response?.data?.errors?.slug?.[0] ||
          err.response?.data?.errors?.Document?.[0] ||
          err.response?.data?.errors?.DocumentName?.[0] ||
          "Saqlashda xato. Backend'dan xabar oling."
      );
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Hujjatni o'chirmoqchimisiz?")) return;

    try {
      await api.delete(
        `/api/teacher/Subjects/DeleteDocument/${subject.slug}/documents/${documentId}`
      );
      toast.success("Hujjat o'chirildi");
      fetchDocuments();
    } catch (err) {
      toast.error("O'chirishda xato");
    }
  };

  const handleDownload = (fileUrl, title) => {
    if (!fileUrl) {
      toast.error("Fayl topilmadi");
      return;
    }
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = title || "hujjat";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Yuklab olish boshlandi");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".menu-icon") &&
        !e.target.closest(".dropdown-menu")
      ) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <PanelLayout>
      <div className="subject-page">
        <div className="tests-header">
          <div className="breadcrumb">
            <span onClick={() => navigate("/teacher")}>
              <TbSmartHome
                style={{ fontSize: 20, color: "#01CC0D", cursor: "pointer" }}
              />
            </span>
            <span className="divider">/</span>
            <span
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer", color: "#01CC0D" }}
            >
              {subject?.fullName || subject?.fullname || "Fan"}
            </span>
            <span className="divider">/</span>
            <span className="current">Hujjatlar</span>
          </div>

          {!showForm && (
            <button className="add-btn" onClick={handleAddNew}>
              <GoPlus style={{ fontSize: 20 }} /> Hujjat qo'shish
            </button>
          )}
        </div>

        {showForm ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="form-container"
          >
            <AddDocumentForm
              initialData={editingDocument}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingDocument(null);
              }}
              requireFile={!editingDocument}
            />
          </motion.div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "120px 0" }}>
            Yuklanmoqda...
          </div>
        ) : documents.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "120px 0",
              color: "#64748b",
            }}
          >
            <h3>Hozircha hujjatlar yo'q</h3>
            <p>"Hujjat qo'shish" tugmasi orqali yangi hujjat qo'shing</p>
          </div>
        ) : (
          <div className="doc-wrap">
            {documents.map((doc) => (
              <motion.div
                key={doc.id}
                className="doc-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {doc.imageUrl ? (
                  <img src={doc.imageUrl} alt={doc.title} className="doc-img" />
                ) : (
                  <div className="doc-img placeholder">📄</div>
                )}

                <div className="doc-header">
                  <h3 className="doc-title" title={doc.title}>
                    {doc.title}
                  </h3>
                  <div className="menu-icon" onClick={() => toggleMenu(doc.id)}>
                    <HiOutlineDotsVertical size={20} />
                  </div>

                  {activeMenu === doc.id && (
                    <div className="dropdown-menu">
                      <p onClick={() => handleDownload(doc.fileUrl, doc.title)}>
                        Ko'rish/Yuklab olish
                      </p>
                      <p onClick={() => handleEdit(doc)}>Tahrirlash</p>
                      <p onClick={() => handleDelete(doc.id)}>O'chirish</p>
                    </div>
                  )}
                </div>

                <p className="doc-desc">{doc.description}</p>

                <button
                  className="download-btn"
                  onClick={() => handleDownload(doc.fileUrl, doc.title)}
                >
                  <IoArrowDownCircleOutline style={{ fontSize: 20 }} />
                  Yuklab olish
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </PanelLayout>
  );
};

export default DocumentsPage;
